import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import "../../../styles/admin.css";
import { FiAlertTriangle } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function AffiliateAdd() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required("*Name is required"),
    email: Yup.string().required("*Email is required"),
    affiliate_code: Yup.string().required("*Affiliate code is required"),
    affiliate_terms: Yup.string().required("*Affiliate terms is required"),
    // account_holder_name: Yup.string().required(
    //   "*Account holder name is required"
    // ),
    // bank_name: Yup.string().required("*Bank name is required"),
    // ifsc_code: Yup.string().required("*IFSC code is required"),
    // account_number: Yup.number()
    //   .typeError("*Account number must be a number")
    //   .required("*Account number is required"),
    affiliate_percentage: Yup.number()
      .typeError("*Account percentage must be a number")
      .required("*Account percentage is required"),
    password: Yup.string()
      .required("*Password is required")
      .min(8, "*Password must be at least 8 characters")
      .matches(/^\S*$/, "*Password must not contain spaces"),

    password_confirmation: Yup.string()
      .required("*Confirm password is required")
      .oneOf([Yup.ref("password"), null], "*Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      affiliate_code: "",
      minimum_withdrawal: "",
      affiliate_percentage: "",
      affiliate_terms: "",
      account_holder_name: "",
      bank_name: "",
      ifsc_code: "",
      account_number: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        ifsc_code: values.ifsc_code,
        bank_name: values.bank_name,
        account_holder_name: values.account_holder_name,
        affiliate_terms: values.affiliate_terms,
        affiliate_percentage: values.affiliate_percentage,
        password: values.password,
        password_confirmation: values.password_confirmation,
        affiliate_code: values.affiliate_code,
        account_number: values.account_number,
        minimum_withdrawal: parseFloat(values.minimum_withdrawal),
      };

      setLoadIndicator(true);
      try {
        const response = await api.post(`admin/account`, payload);
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          navigate("/affiliate");
        } else if (response.status === 422) {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;

          if (status === 422 && data.data?.errors) {
            Object.entries(data.data.errors).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach((msg) => {
                  toast.error(msg, {
                    icon: <FiAlertTriangle className="text-warning" />,
                  });
                });
              }
            });
          } else if (data.errors) {
            toast.error(data.errors, {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          }
        } else {
          toast.error(error.errors.message || "Network or server error", {
            icon: <FiAlertTriangle className="text-danger" />,
          });
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  const Editor = {
    modules: {
      toolbar: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link", "image", "video"],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
      },
    },
  };

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">Add Affiliate</h1>
                  <Link to="/affiliate">
                    <button type="button" className="btn btn-light btn-sm">
                      <span>Back</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5 p-3">
                {/* Name */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${
                      formik.touched.name && formik.errors.name
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter name"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="invalid-feedback">{formik.errors.name}</div>
                  )}
                </div>

                {/* Email */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${
                      formik.touched.email && formik.errors.email
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter email"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="invalid-feedback">
                      {formik.errors.email}
                    </div>
                  )}
                </div>

                {/* Password */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    className={`form-control ${
                      formik.touched.password && formik.errors.password
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter password"
                  />
                  {formik.touched.password && formik.errors.password && (
                    <div className="invalid-feedback">
                      {formik.errors.password}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    name="password_confirmation"
                    className={`form-control ${
                      formik.touched.password_confirmation &&
                      formik.errors.password_confirmation
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.password_confirmation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter password_confirmation"
                  />
                  {formik.touched.password_confirmation &&
                    formik.errors.password_confirmation && (
                      <div className="invalid-feedback">
                        {formik.errors.password_confirmation}
                      </div>
                    )}
                </div>

                {/* Affiliate Code*/}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Affiliate Code<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="affiliate_code"
                    className={`form-control ${
                      formik.touched.affiliate_code &&
                      formik.errors.affiliate_code
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.affiliate_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter affiliate code"
                  />
                  {formik.touched.affiliate_code &&
                    formik.errors.affiliate_code && (
                      <div className="invalid-feedback">
                        {formik.errors.affiliate_code}
                      </div>
                    )}
                </div>

                {/* Affiliate Percentage */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Affiliate Percentage <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="affiliate_percentage"
                    className={`form-control ${
                      formik.touched.affiliate_percentage &&
                      formik.errors.affiliate_percentage
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.affiliate_percentage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter affiliate percentage"
                  />
                  {formik.touched.affiliate_percentage &&
                    formik.errors.affiliate_percentage && (
                      <div className="invalid-feedback">
                        {formik.errors.affiliate_percentage}
                      </div>
                    )}
                </div>

                {/* Account Holder Name */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Account Holder Name 
                  </label>
                  <input
                    type="text"
                    name="account_holder_name"
                    className={`form-control ${
                      formik.touched.account_holder_name &&
                      formik.errors.account_holder_name
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.account_holder_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter account holder name"
                  />
                  {formik.touched.account_holder_name &&
                    formik.errors.account_holder_name && (
                      <div className="invalid-feedback">
                        {formik.errors.account_holder_name}
                      </div>
                    )}
                </div>

                {/* Bank Name */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    name="bank_name"
                    className={`form-control ${
                      formik.touched.bank_name && formik.errors.bank_name
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.bank_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter bank name"
                  />
                  {formik.touched.bank_name && formik.errors.bank_name && (
                    <div className="invalid-feedback">
                      {formik.errors.bank_name}
                    </div>
                  )}
                </div>

                {/* Account Number */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Account Number
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    className={`form-control ${
                      formik.touched.account_number &&
                      formik.errors.account_number
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.account_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter account number"
                  />
                  {formik.touched.account_number &&
                    formik.errors.account_number && (
                      <div className="invalid-feedback">
                        {formik.errors.account_number}
                      </div>
                    )}
                </div>

                {/* IFSC Code*/}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="ifsc_code"
                    placeholder="Enter IFSC code"
                    className={`form-control ${
                      formik.touched.ifsc_code && formik.errors.ifsc_code
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.ifsc_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.ifsc_code && formik.errors.ifsc_code && (
                    <div className="invalid-feedback">
                      {formik.errors.ifsc_code}
                    </div>
                  )}
                </div>

                {/* Minimum Withdrawal*/}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">Minimum Withdrawal</label>
                  <input
                    type="text"
                    name="minimum_withdrawal"
                    placeholder="Enter minimum withdrawal"
                    className={`form-control ${
                      formik.touched.minimum_withdrawal &&
                      formik.errors.minimum_withdrawal
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.minimum_withdrawal}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.minimum_withdrawal &&
                    formik.errors.minimum_withdrawal && (
                      <div className="invalid-feedback">
                        {formik.errors.minimum_withdrawal}
                      </div>
                    )}
                </div>

                {/* Affiliate Terms */}
                <div className="col-md-12 col-12 ">
                  <div className="mb-5">
                    <label className="form-label">
                      <p className=" ">
                        Terms<span className="text-danger">*</span>
                      </p>
                    </label>
                    <ReactQuill
                      value={formik.values.affiliate_terms}
                      onChange={(newContent) => {
                        formik.setFieldValue("affiliate_terms", newContent);
                        formik.validateField("affiliate_terms");
                      }}
                      onBlur={() =>
                        formik.setFieldTouched("affiliate_terms", true)
                      }
                      modules={Editor.modules}
                    />
                    <div className="error text-danger">
                      <small>
                        {formik.touched.affiliate_terms &&
                          formik.errors.affiliate_terms}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Add Affiliate
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default AffiliateAdd;
